import React, { useState } from 'react';
import { MapPin, DollarSign, GraduationCap, Briefcase, ChevronRight } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { countries } from '../utils/mock';

const CountriesV2 = () => {
  const [selectedCountry, setSelectedCountry] = useState(null);

  const countryDetails = {
    1: { // Canada
      workRights: "20 hrs/week during study, full-time on breaks",
      avgTuition: "$15,000 - $35,000 CAD/year",
      postStudyWork: "Up to 3 years PGWP",
      highlights: ["Pathway to PR", "High quality of life", "Multicultural society"]
    },
    2: { // USA
      workRights: "20 hrs/week on-campus",
      avgTuition: "$20,000 - $50,000 USD/year",
      postStudyWork: "12-36 months OPT",
      highlights: ["Top universities", "Research opportunities", "Global recognition"]
    },
    3: { // UK
      workRights: "20 hrs/week during term",
      avgTuition: "$15,000 - $40,000 GBP/year",
      postStudyWork: "2 years PSW visa",
      highlights: ["1-year master's programs", "Rich heritage", "Central location in Europe"]
    },
    4: { // Australia
      workRights: "48 hrs/fortnight during study",
      avgTuition: "$20,000 - $45,000 AUD/year",
      postStudyWork: "2-4 years PSW",
      highlights: ["Beautiful lifestyle", "Strong job market", "PR opportunities"]
    },
    5: { // Europe
      workRights: "Varies by country (15-20 hrs/week)",
      avgTuition: "$0 - $20,000 EUR/year",
      postStudyWork: "6-24 months job seeker visa",
      highlights: ["Affordable education", "Multiple countries", "Schengen benefits"]
    }
  };

  return (
    <section id="countries" className="py-20 bg-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#1565C0" strokeWidth="1"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block mb-4">
            <span className="bg-gradient-to-r from-[#1565C0] to-[#0D47A1] text-white px-6 py-3 rounded-full text-sm font-bold shadow-lg">
              Global Opportunities
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Your World of Possibilities
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Explore top destinations where we've successfully placed thousands of students. Each country offers unique opportunities for your future.
          </p>
        </div>

        {/* Countries Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {countries.map((country, index) => {
            const details = countryDetails[country.id];
            const isSelected = selectedCountry === country.id;
            
            return (
              <Card 
                key={country.id}
                className={`group overflow-hidden border-0 shadow-xl hover:shadow-2xl transition-all duration-500 cursor-pointer ${
                  isSelected ? 'ring-4 ring-[#1565C0] scale-105' : ''
                }`}
                onClick={() => setSelectedCountry(isSelected ? null : country.id)}
              >
                {/* Country Image */}
                <div className="relative h-56 overflow-hidden">
                  <img 
                    src={country.image}
                    alt={country.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                  
                  {/* Country Name Badge */}
                  <div className="absolute top-4 right-4">
                    <div className="bg-white/90 backdrop-blur-sm rounded-full px-4 py-2 flex items-center gap-2">
                      <MapPin size={16} className="text-[#FF7043]" />
                      <span className="font-bold text-gray-900 text-sm">{country.name}</span>
                    </div>
                  </div>

                  {/* Bottom Info */}
                  <div className="absolute bottom-4 left-4 right-4">
                    <p className="text-white/90 text-sm mb-3">{country.description}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 py-1">
                          <div className="text-white font-bold text-lg">{country.programs}</div>
                          <div className="text-white/80 text-xs">Universities</div>
                        </div>
                        <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 py-1">
                          <div className="text-white font-bold text-lg">{country.visaSuccess}</div>
                          <div className="text-white/80 text-xs">Success Rate</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Expandable Details */}
                <div className={`transition-all duration-500 overflow-hidden ${
                  isSelected ? 'max-h-96' : 'max-h-0'
                }`}>
                  <CardContent className="p-6 bg-gradient-to-br from-blue-50 to-white">
                    <div className="space-y-4">
                      {/* Work Rights */}
                      <div className="flex items-start gap-3">
                        <Briefcase size={20} className="text-[#1565C0] mt-1 flex-shrink-0" />
                        <div>
                          <div className="font-semibold text-gray-900 text-sm mb-1">Work Rights</div>
                          <div className="text-gray-600 text-sm">{details.workRights}</div>
                        </div>
                      </div>

                      {/* Tuition */}
                      <div className="flex items-start gap-3">
                        <DollarSign size={20} className="text-[#8BC34A] mt-1 flex-shrink-0" />
                        <div>
                          <div className="font-semibold text-gray-900 text-sm mb-1">Avg. Tuition</div>
                          <div className="text-gray-600 text-sm">{details.avgTuition}</div>
                        </div>
                      </div>

                      {/* Post Study Work */}
                      <div className="flex items-start gap-3">
                        <GraduationCap size={20} className="text-[#FF7043] mt-1 flex-shrink-0" />
                        <div>
                          <div className="font-semibold text-gray-900 text-sm mb-1">Post-Study Work</div>
                          <div className="text-gray-600 text-sm">{details.postStudyWork}</div>
                        </div>
                      </div>

                      {/* Highlights */}
                      <div className="pt-3 border-t border-gray-200">
                        <div className="font-semibold text-gray-900 text-sm mb-2">Key Highlights</div>
                        <div className="flex flex-wrap gap-2">
                          {details.highlights.map((highlight, idx) => (
                            <span key={idx} className="bg-white px-3 py-1 rounded-full text-xs font-medium text-gray-700 border border-gray-200">
                              {highlight}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </div>

                {/* Explore Button */}
                <CardContent className="p-4 bg-white border-t border-gray-100">
                  <button className="w-full flex items-center justify-between text-[#1565C0] font-semibold hover:text-[#FF7043] transition-colors group">
                    <span>{isSelected ? 'Show Less' : 'Explore ' + country.name}</span>
                    <ChevronRight className={`transition-transform duration-300 ${isSelected ? 'rotate-90' : 'group-hover:translate-x-1'}`} size={20} />
                  </button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Quick Comparison */}
        <div className="bg-gradient-to-r from-[#1565C0] to-[#0D47A1] rounded-3xl p-12 text-center relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="dots" width="20" height="20" patternUnits="userSpaceOnUse">
                  <circle cx="2" cy="2" r="2" fill="white"/>
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#dots)" />
            </svg>
          </div>
          <div className="relative z-10">
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Not Sure Which Country is Right for You?
            </h3>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Our experts will analyze your profile, goals, and preferences to recommend the perfect destination.
            </p>
            <button 
              onClick={() => {
                const element = document.getElementById('consultation');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
              className="bg-white text-[#1565C0] px-10 py-5 rounded-full font-bold text-lg hover:bg-gray-100 transition-all duration-300 shadow-xl hover:scale-105 inline-flex items-center gap-3"
            >
              Get Personalized Recommendation
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CountriesV2;
